'use client';

import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useClerk, useUser } from '@clerk/nextjs';
import { ArrowUpRight, Ellipsis, LogOut, Mail, Settings, UserRound } from 'lucide-react';
import { type AccountContextDto } from '@ra-mr/types';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { clerkAppearance } from '@/lib/auth/clerk-appearance';
import { sessionTransition } from '@/lib/auth/session-transition';

interface AccountMenuProps {
  account?: AccountContextDto;
  compact?: boolean;
  showDetailsOnWideScreens?: boolean;
  busy?: boolean;
  runSignOut?: (action: () => Promise<void>) => Promise<void>;
}

export const AccountMenu = ({ account, compact = false, showDetailsOnWideScreens = false, busy = false, runSignOut }: AccountMenuProps) => {
  const { user, isLoaded } = useUser();
  const clerk = useClerk();
  const running = useRef(false);
  const openingSettings = useRef(false);
  const [signingOut, setSigningOut] = useState(false);
  const name = user?.fullName || `${account?.user.firstname ?? ''} ${account?.user.lastname ?? ''}`.trim() || 'Your account';
  const email = user?.primaryEmailAddress?.emailAddress || account?.user.email;

  if (!isLoaded) {
    return <div role="status" className="h-8 w-8 animate-pulse motion-reduce:animate-none rounded-full bg-gray-100"><span className="sr-only">Loading account…</span></div>;
  }
  if (!user) {
    return null;
  }

  return <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button type="button" aria-label={`Account menu for ${name}`} className={cn('flex min-h-11 w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#287d36]', compact && 'h-11 w-11 shrink-0 justify-center rounded-full p-1.5', compact && showDetailsOnWideScreens && 'xl:w-auto xl:justify-start xl:rounded-lg xl:px-2')}>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200 text-gray-600">
          {user?.imageUrl ? <img src={user.imageUrl} alt="" className="h-full w-full object-cover" /> : <UserRound aria-hidden="true" className="h-4 w-4" />}
        </span>
        {(!compact || showDetailsOnWideScreens) && <span className={cn('min-w-0 flex-1', compact && 'hidden xl:block xl:max-w-48')}><span className="block truncate text-sm font-medium text-gray-900">{name}</span><span className="block truncate text-xs text-gray-500">{email}</span></span>}
        {!compact && <Ellipsis aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent onCloseAutoFocus={() => {
      if (openingSettings.current) {
        openingSettings.current = false;
        window.requestAnimationFrame(() => { clerk.openUserProfile({ appearance: clerkAppearance }); });
      }
    }} side={compact ? 'bottom' : 'top'} align={compact ? 'end' : 'start'} sideOffset={10} collisionPadding={12} className="z-[60] w-64 max-w-[calc(100vw-2rem)] rounded-xl p-1.5">
      <DropdownMenuLabel className="px-3 py-2"><span className="block truncate text-sm font-medium">{name}</span><span className="mt-1 block truncate text-xs font-normal text-gray-500">{email}</span></DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem disabled={busy || signingOut} className="gap-3 rounded-md px-3 py-2.5" onSelect={() => {
        openingSettings.current = true;
      }}><Settings aria-hidden="true" className="h-4 w-4" /><span>Account settings</span></DropdownMenuItem>
      {account?.user.role === 'COMPANY_ADMIN' && <DropdownMenuItem asChild disabled={busy || signingOut} className="gap-3 rounded-md px-3 py-2.5"><a href="/company/notifications/"><Mail aria-hidden="true" className="h-4 w-4" /><span>Email notifications</span></a></DropdownMenuItem>}
      <DropdownMenuItem asChild disabled={busy || signingOut} className="gap-3 rounded-md px-3 py-2.5"><a href="/"><ArrowUpRight aria-hidden="true" className="h-4 w-4" /><span>Back to website</span></a></DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem disabled={busy || signingOut} className="gap-3 rounded-md px-3 py-2.5" onSelect={() => {
        const signOut = async () => {
          if (running.current) {
            return;
          }
          running.current = true;
          setSigningOut(true);
          try {
            const action = async () => { await sessionTransition.signOut(clerk); };
            if (runSignOut) {
              await runSignOut(action);
            } else {
              await action();
            }
          } catch {
            toast.error('Unable to sign out. Please try again.');
          } finally {
            running.current = false;
            setSigningOut(false);
          }
        };
        void signOut();
      }}><LogOut aria-hidden="true" className="h-4 w-4" /><span>{signingOut ? 'Signing out…' : 'Sign out'}</span></DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>;
};
